import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  Bot,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Menu,
  Megaphone,
  Network,
  X,
} from 'lucide-react';
import { Button } from './ui/Button';
import { ScrollToTop } from './ScrollToTop';
import { useAuth } from '../contexts/AuthContext';

const solutionLinks = [
  { name: 'Websites', note: 'The front door', path: '/services/web-design', icon: LayoutDashboard },
  { name: 'CRM Workflows', note: 'Capture and follow through', path: '/services/workflows', icon: Network },
  { name: 'AI Receptionist', note: 'Answer and route calls', path: '/services/ai-receptionist', icon: Bot },
  { name: 'Ads & Demand', note: 'Bring in the right traffic', path: '/services/ads', icon: Megaphone },
];

function Wordmark({ showBar = true }: { showBar?: boolean }) {
  return (
    <span className="inline-flex items-center gap-2.5">
      {showBar ? <span className="h-7 w-1 bg-verto-teal" aria-hidden="true" /> : null}
      <span className="font-display text-[1.85rem] font-bold uppercase leading-none tracking-[-0.025em]">Verno <span className={showBar ? '' : 'shoreline-wordmark-accent'}>Web</span></span>
    </span>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const solutionsRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { user, loading: authLoading, signOut } = useAuth();

  useEffect(() => {
    setMobileOpen(false);
    setSolutionsOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (solutionsRef.current && !solutionsRef.current.contains(event.target as Node)) {
        setSolutionsOpen(false);
      }
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const isActive = (path: string) => location.pathname.startsWith(path);
  const compactUtilityShell = ['/login', '/signup', '/auth/callback', '/reset-password', '/dashboard', '/checkout-success'].some((path) =>
    location.pathname.startsWith(path),
  );
  const dashboardSurface = location.pathname.startsWith('/dashboard');
  const darkUtilitySurface =
    location.pathname.startsWith('/blog/') ||
    ['/privacy', '/terms', '/login', '/signup', '/auth/callback', '/reset-password', '/dashboard', '/checkout-success'].some((path) =>
      location.pathname.startsWith(path),
    );
  const campaignLandingPage = location.pathname.startsWith('/lp/client-ready-ai-websites') || location.pathname.startsWith('/lp/remodeling-websites');

  if (campaignLandingPage) {
    return <><ScrollToTop />{children}</>;
  }

  if (compactUtilityShell) {
    return (
      <div className={`site-shell--square flex min-h-screen flex-col ${dashboardSurface ? 'bg-verto-white text-verto-stone' : 'bg-verto-black text-verto-white'}`}>
        <ScrollToTop />
        <header className="border-b border-white/15 bg-verto-black text-verto-white">
          <div className="page-wrap flex h-[4.5rem] items-center justify-between gap-5">
            <Link to="/" aria-label="Verno Web home" className="text-verto-white"><Wordmark /></Link>
            <div className="flex items-center gap-4 text-sm font-semibold">
              {user ? <Link to="/dashboard" className="hover:text-verto-fog">Client portal</Link> : <Link to="/" className="hover:text-verto-fog">Back to site</Link>}
              {user ? <button type="button" onClick={() => signOut()} className="inline-flex items-center gap-1.5 text-verto-white/65 hover:text-verto-white"><LogOut className="h-4 w-4" /> Sign out</button> : null}
            </div>
          </div>
        </header>
        <main className="flex-1">{children}</main>
        <footer className="border-t border-white/15 bg-verto-black py-6 text-verto-white"><div className="page-wrap flex flex-wrap justify-between gap-4 text-xs text-verto-white/45"><p>© {new Date().getFullYear()} Verno Web</p><div className="flex gap-5"><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link></div></div></footer>
      </div>
    );
  }

  return (
    <div className="site-shell site-shell--shoreline site-shell--square flex min-h-screen flex-col">
      <ScrollToTop />
      <header className="reference-home-header sticky top-0 z-50 border-b border-verto-black/10 bg-verto-white/95 text-verto-black backdrop-blur-md">
        <div className="page-wrap flex h-[4.5rem] items-center justify-between gap-6">
          <Link to="/" aria-label="Verno Web home">
            <Wordmark showBar={false} />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary navigation">
            <div className="relative" ref={solutionsRef}>
              <button
                type="button"
                className={`inline-flex items-center gap-1.5 text-sm font-semibold transition hover:text-verto-deep ${isActive('/services') ? 'text-verto-deep' : ''}`}
                onClick={() => setSolutionsOpen((open) => !open)}
                aria-expanded={solutionsOpen}
              >
                Solutions
                <ChevronDown className={`h-4 w-4 transition ${solutionsOpen ? 'rotate-180' : ''}`} />
              </button>
              {solutionsOpen && (
                <div className="absolute left-1/2 top-full mt-5 w-[34rem] -translate-x-1/2 rounded-xl bg-verto-black p-3 text-verto-white shadow-[0_24px_60px_rgba(23,23,19,0.24)]">
                  <div className="grid grid-cols-2 gap-1">
                    {solutionLinks.map(({ name, note, path, icon: Icon }) => (
                      <Link key={path} to={path} className="group flex gap-3 rounded-lg p-4 hover:bg-white/[0.08]">
                        <Icon className="mt-0.5 h-5 w-5 text-verto-mint" />
                        <span>
                          <span className="block font-semibold">{name}</span>
                          <span className="mt-1 block text-xs text-verto-white/55">{note}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                  <Link to="/services" className="mt-2 flex items-center justify-between border-t border-white/15 px-4 pt-4 text-sm font-semibold text-verto-mint">
                    See the connected system
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              )}
            </div>
            <Link to="/demos" className={`text-sm font-semibold transition hover:text-verto-deep ${isActive('/demos') ? 'text-verto-deep' : ''}`}>Work</Link>
            <Link to="/#process" className="text-sm font-semibold transition hover:text-verto-deep">How it works</Link>
            <Link to="/pricing" className={`text-sm font-semibold transition hover:text-verto-deep ${isActive('/pricing') ? 'text-verto-deep' : ''}`}>Packages &amp; scope</Link>
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            {!authLoading && (
              user ? (
                <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-semibold hover:text-verto-deep">
                  <LayoutDashboard className="h-4 w-4" /> Portal
                </Link>
              ) : (
                <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold hover:text-verto-deep">
                  Log in
                </Link>
              )
            )}
            <Button asChild size="sm">
              <Link to="/free-demo">Request a free demo</Link>
            </Button>
          </div>

          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white/25 lg:hidden"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="shoreline-mobile-panel absolute inset-x-0 top-[4.5rem] max-h-[calc(100vh-4.5rem)] overflow-y-auto border-b p-5 shadow-[0_24px_50px_rgba(1,12,8,0.32)] lg:hidden">
              <div className="shoreline-mobile-menu page-wrap">
              <p className="shoreline-mobile-menu__title">Solutions</p>
              <div className="shoreline-mobile-menu__solutions">
                {solutionLinks.map(({ name, note, path, icon: Icon }) => (
                  <Link key={path} to={path}>
                    <Icon aria-hidden="true" />
                    <span><span>{name}</span><small>{note}</small></span>
                  </Link>
                ))}
              </div>
              <div className="shoreline-mobile-menu__secondary">
                <Link to="/demos">Work</Link>
                <Link to="/#process">How it works</Link>
                <Link to="/pricing">Packages &amp; scope</Link>
                <Link to="/about">About</Link>
              </div>
              <Button asChild size="lg" className="mt-4 w-full">
                <Link to="/free-demo">Request a free demo <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              {!authLoading && (
                <div className="mt-4 flex justify-center">
                  {user ? (
                    <div className="flex items-center gap-5 text-sm font-semibold">
                      <Link to="/dashboard">Client portal</Link>
                      <button type="button" onClick={() => signOut()} className="inline-flex items-center gap-1.5"><LogOut className="h-4 w-4" /> Sign out</button>
                    </div>
                  ) : <Link to="/login" className="text-sm font-semibold">Client log in</Link>}
                </div>
              )}
              </div>
          </div>
        )}
      </header>

      <main className={`shoreline-marketing-main flex-1 ${darkUtilitySurface ? 'shoreline-marketing-main--dark bg-verto-black text-verto-white' : ''}`}>{children}</main>

      <footer className="shoreline-footer">
          <div className="shoreline-wrap">
            <div className="shoreline-footer__top">
              <div><Wordmark /><p>Custom websites and the response paths behind them, built directly with a Seattle-based partner.</p></div>
              <nav className="shoreline-footer__links" aria-label="Footer navigation">
                <Link to="/services/web-design">Websites</Link><Link to="/services/workflows">Workflows</Link><Link to="/demos">Work</Link><Link to="/about">About</Link><Link to="/login">Client portal</Link>
              </nav>
            </div>
            <div className="shoreline-footer__bottom"><span>© {new Date().getFullYear()} Verno Web. All rights reserved.</span><div><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link></div></div>
          </div>
        </footer>
    </div>
  );
}
